// Shared, client-safe types + helpers for localized (multi-currency) pricing.
// No server-only imports here so both client components and server code can
// import from this module.

export type Money = { amount: string; currencyCode: string };

export type LocalizedSubscriptionPrice = { monthly?: Money; annual?: Money };

export type LocalizedPricing = {
  /** ISO 3166-1 alpha-2 country the prices were contextualized for. */
  countryCode: string;
  /** Presentment currency of the returned prices (e.g. "EUR"). */
  currencyCode: string | null;
  /** variantId -> price (one-time token packs). */
  tokenPacks: Record<string, Money>;
  /** plan tier -> { monthly, annual } recurring prices. */
  subscriptions: Record<string, LocalizedSubscriptionPrice>;
};

// Vercel / geo-IP sentinels that aren't real purchasable countries (Tor exit,
// anonymizing proxy, unknown, and the EU pseudo-code). Treated as "no country".
const COUNTRY_SENTINELS = new Set(["XX", "T1", "ZZ", "A1", "A2", "O1", "AP", "EU"]);

/** Validate/normalize an ISO 3166-1 alpha-2 country code; null if unusable. */
export function normalizeCountryCode(raw: unknown): string | null {
  if (typeof raw !== "string") return null;
  const code = raw.trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(code)) return null;
  if (COUNTRY_SENTINELS.has(code)) return null;
  return code;
}

/**
 * Format a Shopify presentment price for display. IMPORTANT: Shopify already
 * applies per-market rounding to presentment prices and those rounding rules
 * aren't readable via API — so we only FORMAT the amount for display, never
 * re-round or recompute it (that would desync from the Shopify checkout total).
 */
export function formatMoney(money: Money | null | undefined): string | null {
  if (!money) return null;
  const value = Number(money.amount);
  if (!Number.isFinite(value)) return null;
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: money.currencyCode,
    }).format(value);
  } catch {
    // Unknown currency code — fall back to a plain, unambiguous string.
    return `${money.currencyCode} ${value.toFixed(2)}`;
  }
}

/**
 * Derived "per month" figure for an annual price (annual total / 12). This is a
 * marketing display number only (never charged), mirroring how the hardcoded
 * USD `effectiveMonthly` strings were derived from the annual price.
 */
export function deriveEffectiveMonthly(annual: Money | null | undefined): string | null {
  if (!annual) return null;
  const value = Number(annual.amount);
  if (!Number.isFinite(value)) return null;
  return formatMoney({ amount: String(value / 12), currencyCode: annual.currencyCode });
}
